import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { ApiFout } from '../api/fouten.ts';

/**
 * Zet de tekst uit een invoerveld om naar een getal, of `null` als het veld leeg is of geen getal bevat.
 * @param waarde De tekst.
 * @returns Het getal, of `null`.
 */
export function naarGetal(waarde: string | number | null | undefined): number | null {
  if (typeof waarde === 'number') {
    return Number.isNaN(waarde) ? null : waarde;
  }
  if (typeof waarde !== 'string' || waarde.trim() === '') {
    return null;
  }
  const getal = Number(waarde.replace(',', '.'));
  return Number.isNaN(getal) ? null : getal;
}

/**
 * Zet een lege tekst om naar `null`, zodat een leeg veld een waarde wist.
 * @param waarde De tekst.
 * @returns De tekst, of `null` als hij leeg is.
 */
export function leegNaarNull<T>(waarde: T | '' | null | undefined): T | null {
  return waarde === '' || waarde === null || waarde === undefined ? null : waarde;
}

/**
 * Geeft een getal terug als tekst voor in een invoerveld.
 * @param waarde Het getal, of leeg.
 * @returns De tekst, of een lege tekst.
 */
export function naarTekst(waarde: number | string | null | undefined): string {
  return waarde === null || waarde === undefined ? '' : String(waarde);
}

/**
 * Koppelt de veldfouten uit een `ApiFout` aan de velden van een formulier. Fouten bij velden die het formulier
 * niet kent, komen terug zodat ze bovenaan getoond kunnen worden.
 * @param fout De opgevangen fout.
 * @param setError De `setError` van react-hook-form.
 * @param velden De namen van de velden in het formulier.
 * @returns De melding voor bovenaan het formulier, of `null` als alle fouten bij een veld horen.
 */
export function pasVeldfoutenToe<T extends FieldValues>(
  fout: unknown,
  setError: UseFormSetError<T>,
  velden: readonly string[],
): string | null {
  if (!(fout instanceof ApiFout)) {
    return fout instanceof Error ? fout.message : 'Er ging iets mis.';
  }
  const overig: string[] = [];
  for (const [pad, melding] of Object.entries(fout.veldfouten)) {
    const veld = velden.find((naam) => naam === pad || pad.endsWith(`.${naam}`));
    if (veld) {
      setError(veld as Path<T>, { type: 'server', message: melding });
    } else {
      overig.push(`${pad}: ${melding}`);
    }
  }
  if (overig.length > 0) {
    return `${fout.message} ${overig.join('; ')}`;
  }
  return Object.keys(fout.veldfouten).length > 0 ? null : fout.message;
}

/**
 * Vandaag als ISO-datum in de tijdzone van de browser, voor datumvelden.
 * @returns Bijvoorbeeld `2026-11-04`.
 */
export function vandaag(): string {
  const nu = new Date();
  const maand = String(nu.getMonth() + 1).padStart(2, '0');
  const dag = String(nu.getDate()).padStart(2, '0');
  return `${nu.getFullYear()}-${maand}-${dag}`;
}

/**
 * Het huidige moment als waarde voor een `datetime-local`-veld.
 * @returns Bijvoorbeeld `2026-11-04T09:30`.
 */
export function nuLokaal(): string {
  const nu = new Date();
  nu.setSeconds(0, 0);
  const verschoven = new Date(nu.getTime() - nu.getTimezoneOffset() * 60_000);
  return verschoven.toISOString().slice(0, 16);
}

/**
 * Zet een ISO-tijdstip om naar de waarde van een `datetime-local`-veld.
 * @param iso Het tijdstip.
 * @returns De lokale tijd zonder tijdzone.
 */
export function naarLokaalTijdstip(iso: string): string {
  const moment = new Date(iso);
  const verschoven = new Date(moment.getTime() - moment.getTimezoneOffset() * 60_000);
  return verschoven.toISOString().slice(0, 16);
}

/**
 * Zet de waarde van een `datetime-local`-veld om naar een ISO-tijdstip met tijdzone.
 * @param lokaal De lokale tijd, zoals `2026-11-04T09:30`.
 * @returns Het ISO-tijdstip, of `undefined` als het veld leeg is.
 */
export function vanLokaalTijdstip(lokaal: string): string | undefined {
  return lokaal ? new Date(lokaal).toISOString() : undefined;
}
